import { defaultBoxPageCopy, formatAmount, OPENER_IDLE_CONTROLS } from "@loot-os/api-contract";
import { BoxHero } from "@loot-os/design-system/domain/BoxHero";
import { AppLink } from "@loot-os/design-system/foundations/AppLink";
import type { BoxPageHeading } from "@loot-os/design-system/templates/box/boxPageContent";
import { BoxPageHeadingMeta } from "@loot-os/design-system/templates/box/sections/heading/BoxPageHeading";
import type { OpenerIdleProps } from "@loot-os/react";
import { useEffect } from "react";

import { useCurrency } from "../lib/currency.js";
import { useTerminology } from "../lib/terminology.js";

/**
 * The box before it's opened, drawn by the design system's BoxHero in the opener's idle slot
 * (`renderIdle`): the art with what's inside drifting behind, the name, price and meta row the
 * page's heading would show, then the quantity and Open. While it's up the heading steps aside
 * (`onShown`), so nothing is said twice, unless the layout keeps its heading in place (the
 * stacked page, `BoxHeroHeadingContext` off), where the hero leaves the name out instead; the
 * reveal and the results are the opener's own.
 */
export function BoxIdleHero({
  idle,
  heading,
  onShown,
}: {
  idle: OpenerIdleProps;
  /** The page's heading, ready: its price note and meta row. */
  heading: BoxPageHeading | null;
  onShown: (shown: boolean) => void;
}) {
  const currency = useCurrency();
  const terms = useTerminology();
  const { fairness } = defaultBoxPageCopy({ terms, currencyPlural: currency.namePlural }).opener;
  useEffect(() => {
    onShown(true);
    return () => onShown(false);
  }, [onShown]);

  const box = idle.boxData;
  const items = box.dropTable
    .filter((item) => item.weight > 0 && item.imageUrl)
    .map((item, i) => ({ id: `${item.itemId}-${i}`, image: item.imageUrl }));
  const total = box.priceCents * idle.quantity;

  return (
    <BoxHero
      name={box.name}
      price={box.priceCents}
      priceNote={heading?.priceNote}
      meta={
        heading ? (
          <BoxPageHeadingMeta
            creator={heading.creator}
            version={heading.version}
            actions={heading.actions}
          />
        ) : undefined
      }
      art={box.imageUrl}
      items={items}
      quantity={
        idle.quantities.length > 1
          ? {
              label: OPENER_IDLE_CONTROLS.quantity,
              options: idle.quantities,
              value: idle.quantity,
              onValueChange: idle.setQuantity,
            }
          : undefined
      }
      open={{
        label: idle.demo
          ? OPENER_IDLE_CONTROLS.openDemo
          : idle.prepaid
            ? OPENER_IDLE_CONTROLS.openPrepaid
            : OPENER_IDLE_CONTROLS.openFor(formatAmount(total, currency)),
        onClick: idle.open,
        disabled: idle.requesting,
        loading: idle.requesting,
      }}
      fastOpen={
        idle.quickReveal
          ? {
              label: OPENER_IDLE_CONTROLS.fastOpen,
              checked: idle.quickReveal.on,
              onCheckedChange: idle.quickReveal.set,
            }
          : undefined
      }
      footnote={
        <>
          {fairness.statement} · <AppLink href="/fairness">{fairness.link}</AppLink>
        </>
      }
    />
  );
}
